import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import axiosInstance from "../../../../api/axiosInstance";

const OPTIONS = [
  { value: "AVAILABLE", label: "Available", dot: "#22C55E", bg: "bg-[#ECFDF3]", text: "text-[#16A34A]", border: "border-[#A7F3C0]" },
  { value: "AWAY", label: "Away", dot: "#F59E0B", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  { value: "ON_BREAK", label: "On Break", dot: "#F97316", bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
  { value: "OFFLINE", label: "Offline", dot: "#94A3B8", bg: "bg-gray-100", text: "text-gray-600", border: "border-gray-200" },
];

const HEARTBEAT_MS = 60000; // ping at most once a minute, only if active

export default function AvailabilityControl() {
  const [status, setStatus] = useState("AVAILABLE");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Load current availability
  useEffect(() => {
    axiosInstance
      .get("/api/v1/support/availability")
      .then((r) => {
        const v = r.data?.body?.availability;
        if (v) setStatus(v);
      })
      .catch(() => {});
  }, []);

  // Track real user activity (so "idle at desk" stops the heartbeat → backend
  // flips to Away; coming back fires a ping → backend flips back to Available).
  useEffect(() => {
    const mark = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousemove", "keydown", "click", "scroll"];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, mark));
  }, []);

  // Activity-gated heartbeat
  useEffect(() => {
    const tick = () => {
      if (Date.now() - lastActivityRef.current > HEARTBEAT_MS) return; // idle → skip
      axiosInstance
        .post("/api/v1/support/heartbeat")
        .then((r) => {
          const v = r.data?.body?.availability;
          if (v) setStatus((prev) => (prev === "AWAY" ? v : prev)); // reflect auto-return
        })
        .catch(() => {});
    };
    const id = setInterval(tick, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const change = async (v) => {
    setStatus(v);
    setOpen(false);
    try {
      await axiosInstance.patch("/api/v1/support/availability", { availability: v });
    } catch {
      /* no-op */
    }
  };

  const cur = OPTIONS.find((o) => o.value === status) || OPTIONS[0];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${cur.bg} ${cur.text} border-[#CDBEF5]`}
        title="Your availability for ticket routing"
      >
        <span className="w-2 h-2 rounded-full" style={{ background: cur.dot }} />
        {cur.label}
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-white rounded-lg border border-gray-200 shadow-lg z-50 overflow-hidden">
          {OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => change(o.value)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left text-gray-700"
            >
              <span className="w-2 h-2 rounded-full" style={{ background: o.dot }} />
              {o.label}
              {o.value === status && <Check className="w-3.5 h-3.5 ml-auto text-[#0369A1]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
